import React, { useState, useEffect } from 'react';
import apiService, { errorHelpers, formatHelpers } from '../../../utils/apiService';

const VerificationHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [alert, setAlert] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(null);

  useEffect(() => {
    loadHistory(currentPage);
  }, [currentPage]);

  const loadHistory = async (page = 1) => {
    setLoading(true);
    try {
      const response = await apiService.verification.getHistory(page, 10);
      setHistory(response.data.history);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load verification history:', error);
      setAlert({
        type: 'error',
        message: 'Failed to load verification history'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return '✅';
      case 'failed': return '❌';
      case 'pending': return '⏳';
      default: return '📄';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-success';
      case 'failed': return 'text-danger';
      case 'pending': return 'text-warning';
      default: return 'text-muted';
    }
  };

  const getStatusText = (result, paymentStatus) => {
    if (paymentStatus !== 'paid') return 'Payment Pending';
    return result === 'success' ? 'Certificate Found' : 'Certificate Not Found';
  };

  // Safe date formatting function
  const formatGraduationDate = (dateString) => {
    if (!dateString) return 'N/A';

    // If it's already a formatted string (like "11th January, 2024"), return as-is
    if (typeof dateString === 'string' && dateString.includes(',')) {
      return dateString;
    }

    // Try to format it as a date if it's in ISO format
    try {
      return formatHelpers.formatDate(dateString);
    } catch (error) {
      // If formatting fails, return the original string
      return dateString;
    }
  };

  // Function to print verification template
  const handlePrintTemplate = async (record) => {
    if (!record.bpms_transaction_id) {
      setAlert({
        type: 'error',
        message: 'Cannot print: Transaction ID not found'
      });
      return;
    }

    setLoadingTemplate(record.id);
    try {
      // Get the verification status with template
      const response = await apiService.verification.checkVerificationStatus(record.bpms_transaction_id);

      if (response.data.verification_template) {
        // Create a new window for printing
        const printWindow = window.open('', '_blank', 'width=800,height=600');

        const template = response.data.verification_template;
        const certificate = response.data.certificate;

        printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>Certificate Verification - ${certificate.certificate_number}</title>
                        <style>
                            body {
                                font-family: 'Times New Roman', serif;
                                font-size: 14px;
                                line-height: 1.6;
                                color: #000;
                                max-width: 800px;
                                margin: 0 auto;
                                padding: 20px;
                            }
                            .header {
                                text-align: center;
                                margin-bottom: 30px;
                            }
                            .university-logo {
                                width: 80px;
                                height: 80px;
                                margin: 0 auto 15px;
                                display: block;
                            }
                            .university-name {
                                font-size: 18px;
                                font-weight: bold;
                                margin-bottom: 5px;
                            }
                            .university-address {
                                font-size: 12px;
                                margin-bottom: 20px;
                            }
                            .ref-date {
                                display: flex;
                                justify-content: space-between;
                                margin-bottom: 20px;
                                font-weight: bold;
                            }
                            .recipient {
                                margin-bottom: 20px;
                                line-height: 1.4;
                            }
                            .recipient-line {
                                margin-bottom: 2px;
                            }
                            .recipient-company {
                                font-weight: bold;
                                margin-bottom: 5px;
                            }
                            .subject {
                                text-align: center;
                                font-weight: bold;
                                text-decoration: underline;
                                margin: 20px 0;
                            }
                            .content {
                                margin: 20px 0;
                            }
                            .certificate-table {
                                width: 100%;
                                border-collapse: collapse;
                                margin: 20px 0;
                            }
                            .certificate-table th,
                            .certificate-table td {
                                border: 1px solid #000;
                                padding: 8px;
                                text-align: left;
                            }
                            .certificate-table th {
                                background-color: #f5f5f5;
                                font-weight: bold;
                            }
                            .signature {
                                margin-top: 30px;
                            }
                            .signature p {
                                margin: 5px 0;
                            }
                            @media print {
                                body { margin: 0; }
                                .no-print { display: none; }
                            }
                                .signature-container {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: 40px;
    page-break-inside: avoid;
    break-inside: avoid;
}

.signature-section {
    flex: 1;
    page-break-inside: avoid;
    break-inside: avoid;
}

.stamp-section {
    margin-left: 20px;
    flex-shrink: 0;
    page-break-inside: avoid;
    break-inside: avoid;
}

@media print {
    body { 
        margin: 0; 
        font-size: 12px;
        line-height: 1.4;
    }
    .no-print { 
        display: none; 
    }
    .signature-container {
        page-break-inside: avoid;
        break-inside: avoid;
        margin-top: 20px;
    }
    .signature-section,
    .stamp-section {
        page-break-inside: avoid;
        break-inside: avoid;
    }
    @page {
        margin: 0.5in;
        size: A4;
    }
}
                        </style>
                    </head>
                    <body>
                        <div class="no-print" style="text-align: center; margin-bottom: 20px;">
                            <button onclick="window.print()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer;">
                                🖨️ Print Document
                            </button>
                            <button onclick="window.close()" style="padding: 10px 20px; background: #6c757d; color: white; border: none; border-radius: 5px; cursor: pointer; margin-left: 10px;">
                                ✖️ Close
                            </button>
                        </div>
                        
                        <div class="header">
                           <img src="${template.assets?.logo || '/images/oou_logo.png'}" alt="OOU Logo" class="university-logo" onerror="this.style.display='none'">
                            <div class="university-name">${template.header.institution}</div>
                            <div class="university-address">${template.header.address}</div>
                        </div>
                        
                        <div class="ref-date">
                            <div><strong>Our Ref: ${template.header.ref_number}</strong></div>
                            <div><strong>Date: ${template.header.date}</strong></div>
                        </div>
                        
                        <div class="recipient">
                            <div class="recipient-company">${template.recipient.company}</div>
                            ${template.recipient.address.map(line => `<div class="recipient-line">${line}</div>`).join('')}
                        </div>
 
                        <div class="subject">RE: ${template.subject}</div>

                        <div class="content">
                            <div class="student-details">
                                <table style="width: 100%; margin: 20px 0; border: none;">
                                    <tr>
                                        <td style="border: none; padding: 5px 0; width: 150px;"><strong>NAME:</strong></td>
                                        <td style="border: none; padding: 5px 0; padding-left: 20px;">${template.content.student_info.name}</td>
                                    </tr>
                                    <tr>
                                        <td style="border: none; padding: 5px 0;"><strong>MATRIC NO:</strong></td>
                                        <td style="border: none; padding: 5px 0; padding-left: 20px;">${template.content.student_info.matric_number}</td>
                                    </tr>
                                    <tr>
                                        <td style="border: none; padding: 5px 0;"><strong>COURSE:</strong></td>
                                        <td style="border: none; padding: 5px 0; padding-left: 20px;">${template.content.student_info.course}</td>
                                    </tr>
                                </table>
                            </div>
                            
                            <p style="margin: 30px 0 20px 0;">${template.content.confirmation_text}</p>
                            <p style="margin: 20px 0;">${template.content.closing}</p>
                        </div>
                        
                        <div class="signature">
                            <div class="signature-container">
                            <div class="signature-section">
                                ${template.signature.signature_image ?
            `<img src="${template.signature.signature_image}" alt="Signature" style="max-width: 150px; height: auto; margin-bottom: 10px;" onerror="this.style.display='none'">` :
            '<div style="height: 60px; border-bottom: 1px solid #000; width: 200px; margin-bottom: 10px;"></div>'
          }
                                <p><em><strong>${template.signature.name}</strong></em></p>
                                <p><em><strong>${template.signature.title}</strong></em></p>
                                <p><em><strong>${template.signature.for}</strong></em></p>
                                <p style="font-size: 12px;">${template.signature.email}</p>
                            </div>
                            <div class="stamp-section">
                                ${template.signature.stamp_image ?
            `<img src="${template.signature.stamp_image}" alt="Official Stamp" style="max-width: 120px; height: auto;" onerror="this.style.display='none'">` :
            '<div style="width: 120px; height: 120px; border: 2px solid #000; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; text-align: center;">OFFICIAL<br>STAMP</div>'
          }
                            </div>
                        </div>
                    </div>
                    </body>
                    </html>
 
                `);

        printWindow.document.close();
        printWindow.focus();
      } else {
        setAlert({
          type: 'error',
          message: 'Verification template not available for this certificate'
        });
      }
    } catch (error) {
      console.error('Error loading verification template:', error);
      setAlert({
        type: 'error',
        message: 'Failed to load verification template'
      });
    } finally {
      setLoadingTemplate(null);
    }
  };

  if (loading && history.length === 0) {
    return (
      <div className="verification-history">
        <div className="history-header">
          <h2>📋 Verification History</h2>
          <p>Your certificate verification records</p>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading verification history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="verification-history">
      <div className="history-header">
        <h2>📋 Verification History</h2>
        <p>Your certificate verification records and payment history</p>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type} mb-4`}>
          {alert.message}
          <button
            onClick={() => setAlert(null)}
            className="btn-close"
            aria-label="Close"
          >×</button>
        </div>
      )}

      {history.length === 0 ? (
        <div className="no-history">
          <div className="no-history-icon">📋</div>
          <h3>No Verification History</h3>
          <p>You haven't verified any certificates yet. Start by verifying your first certificate!</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.hash = 'verify'}
          >
            Verify Certificate
          </button>
        </div>
      ) : (
        <>
          <div className="history-stats">
            <div className="stat-item">
              <span className="stat-number">{pagination?.total || 0}</span>
              <span className="stat-label">Total Verifications</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {history.filter(h => h.authentication_result === 'success').length}
              </span>
              <span className="stat-label">Successful</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {history.filter(h => h.authentication_result === 'failed').length}
              </span>
              <span className="stat-label">Not Found</span>
            </div>
          </div>

          <div className="history-table-container">
            <div className="table-responsive">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Certificate Number</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Payment Ref</th>
                    <th>Certificate Details</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((record) => (
                    <tr key={record.id}>
                      <td>
                        <div className="certificate-number">
                          {record.certificate_number}
                        </div>
                      </td>
                      <td>
                        <div className="verification-date">
                          {formatHelpers.formatDateTime(record.authenticated_at)}
                        </div>
                      </td>
                      <td>
                        <div className={`status-badge ${getStatusColor(record.authentication_result)}`}>
                          <span className="status-icon">
                            {getStatusIcon(record.authentication_result)}
                          </span>
                          <span className="status-text">
                            {getStatusText(record.authentication_result, record.payment_status)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="amount">
                          {formatHelpers.formatCurrency(record.amount_paid)}
                        </div>
                      </td>
                      <td>
                        <div className="payment-reference">
                          {record.payment_reference || 'N/A'}
                        </div>
                      </td>
                      <td>
                        {record.certificate_details ? (
                          <div className="certificate-summary">
                            <div className="student-name">
                              <strong>{record.certificate_details.student_name}</strong>
                            </div>
                            <div className="course">
                              {record.certificate_details.course}
                            </div>
                            <div className="graduation-date">
                              <small>Graduated: {formatGraduationDate(record.certificate_details.graduation_date)}</small>
                            </div>
                            {record.certificate_details.grade && (
                              <div className="grade">
                                <small>Grade: {record.certificate_details.grade}</small>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">Certificate not found</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          {record.authentication_result === 'success' && record.certificate_details && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handlePrintTemplate(record)}
                              disabled={loadingTemplate === record.id}
                              title="Print Official Verification Letter"
                            >
                              {loadingTemplate === record.id ? (
                                <span className="loading-spinner-sm"></span>
                              ) : (
                                '🖨️'
                              )}
                              Print
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.has_prev || loading}
              >
                ← Previous
              </button>

              <div className="pagination-info">
                Page {pagination.page} of {pagination.pages}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.has_next || loading}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      <style jsx>{`
                .verification-history {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 1rem;
                }

                .history-header {
                    margin-bottom: 2rem;
                }

                .history-header h2 {
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: #1f2937;
                    margin-bottom: 0.5rem;
                }

                .history-header p {
                    color: #6b7280;
                }

                .alert {
                    padding: 1rem;
                    border-radius: 0.5rem;
                    margin-bottom: 1rem;
                    position: relative;
                }

                .alert-error {
                    background-color: #fef2f2;
                    border: 1px solid #fecaca;
                    color: #dc2626;
                }

                .btn-close {
                    position: absolute;
                    top: 0.5rem;
                    right: 0.5rem;
                    background: none;
                    border: none;
                    font-size: 1.25rem;
                    cursor: pointer;
                    color: inherit;
                }

                .loading-container {
                    text-align: center;
                    padding: 3rem;
                    background: white;
                    border-radius: 0.75rem;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }

                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 4px solid #e5e7eb;
                    border-top: 4px solid #3b82f6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 1rem;
                }

                .loading-spinner-sm {
                    display: inline-block;
                    width: 16px;
                    height: 16px;
                    border: 2px solid #e5e7eb;
                    border-top: 2px solid #3b82f6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-right: 0.5rem;
                }

                .no-history {
                    text-align: center;
                    padding: 4rem 2rem;
                    background: white;
                    border-radius: 0.75rem;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }

                .no-history-icon {
                    font-size: 4rem;
                    margin-bottom: 1.5rem;
                }

                .history-stats {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                    gap: 1rem;
                    margin-bottom: 2rem;
                }

                .stat-item {
                    background: white;
                    padding: 1.5rem;
                    border-radius: 0.75rem;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                    text-align: center;
                }

                .stat-number {
                    display: block;
                    font-size: 2rem;
                    font-weight: 700;
                    color: #3b82f6;
                    line-height: 1;
                    margin-bottom: 0.5rem;
                }

                .stat-label {
                    font-size: 0.875rem;
                    color: #6b7280;
                    font-weight: 500;
                }

                .history-table-container {
                    background: white;
                    border-radius: 0.75rem;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                }

                .table-responsive {
                    overflow-x: auto;
                }

                .history-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.875rem;
                }

                .history-table th {
                    background-color: #f8fafc;
                    padding: 1rem 0.75rem;
                    text-align: left;
                    font-weight: 600;
                    color: #374151;
                    border-bottom: 2px solid #e5e7eb;
                    white-space: nowrap;
                }

                .history-table td {
                    padding: 1rem 0.75rem;
                    border-bottom: 1px solid #e5e7eb;
                    vertical-align: top;
                }

                .history-table tr:hover {
                    background-color: #f9fafb;
                }

                .certificate-number {
                    font-family: 'Courier New', monospace;
                    font-weight: 600;
                    color: #1f2937;
                }

                .verification-date {
                    color: #6b7280;
                    white-space: nowrap;
                }

                .status-badge {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.25rem 0.5rem;
                    border-radius: 0.375rem;
                    font-size: 0.875rem;
                    font-weight: 500;
                    white-space: nowrap;
                }

                .text-success {
                    background-color: #dcfce7;
                    color: #16a34a;
                }

                .text-danger {
                    background-color: #fef2f2;
                    color: #dc2626;
                }

                .text-warning {
                    background-color: #fef3c7;
                    color: #d97706;
                }

                .text-muted {
                    color: #6b7280;
                }

                .amount {
                    font-weight: 600;
                    color: #1f2937;
                    white-space: nowrap;
                }

                .payment-reference {
                    font-family: 'Courier New', monospace;
                    font-size: 0.75rem;
                    color: #6b7280;
                    word-break: break-all;
                    max-width: 120px;
                }

                .certificate-summary {
                    max-width: 250px;
                }

                .student-name {
                    color: #1f2937;
                    margin-bottom: 0.25rem;
                }

                .course {
                    color: #4b5563;
                    margin-bottom: 0.25rem;
                    font-size: 0.8rem;
                }

                .graduation-date, .grade {
                    color: #6b7280;
                    font-size: 0.75rem;
                }

                .action-buttons {
                    display: flex;
                    gap: 0.5rem;
                    flex-direction: column;
                }

                .btn {
                    padding: 0.5rem 1rem;
                    border-radius: 0.375rem;
                    border: none;
                    cursor: pointer;
                    font-size: 0.875rem;
                    font-weight: 500;
                    transition: all 0.2s;
                    text-decoration: none;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    justify-content: center;
                    white-space: nowrap;
                }

                .btn-primary {
                    background-color: #3b82f6;
                    color: white;
                }

                .btn-primary:hover {
                    background-color: #2563eb;
                }

                .btn-outline-primary {
                    background-color: transparent;
                    color: #3b82f6;
                    border: 1px solid #3b82f6;
                }

                .btn-outline-primary:hover {
                    background-color: #3b82f6;
                    color: white;
                }

                .btn-sm {
                    padding: 0.375rem 0.75rem;
                    font-size: 0.75rem;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .pagination {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 1rem;
                    margin-top: 2rem;
                    padding: 2rem 0;
                }

                .pagination-btn {
                    background: white;
                    border: 2px solid #e5e7eb;
                    color: #374151;
                    padding: 0.5rem 1rem;
                    border-radius: 0.5rem;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .pagination-btn:hover:not(:disabled) {
                    border-color: #3b82f6;
                    color: #3b82f6;
                }

                .pagination-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .pagination-info {
                    font-weight: 500;
                    color: #374151;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @media (max-width: 768px) {
                    .history-table {
                        font-size: 0.75rem;
                    }

                    .history-table th,
                    .history-table td {
                        padding: 0.5rem 0.25rem;
                    }

                    .certificate-summary {
                        max-width: 150px;
                    }

                    .payment-reference {
                        max-width: 80px;
                    }

                    .history-stats {
                        grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
                    }

                    .pagination {
                        flex-direction: column;
                        gap: 0.5rem;
                    }

                    .pagination-btn {
                        width: 100%;
                        max-width: 200px;
                    }
                }
            `}</style>
    </div>
  );
};

export default VerificationHistory;